import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Users, Briefcase, Sparkles, AlertCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { VEHICLE_CLASSES } from "@/lib/vehicleClasses";
import { buildQuotePayload, fetchQuote, money } from "@/lib/quote";

// Map engine key -> catalogue entry (id in vehicleClasses.js is the engine key).
const CATALOG = Object.fromEntries(VEHICLE_CLASSES.map((v) => [v.id, v]));

// Display name (from vehicleClasses) -> engine key
const NAME_TO_KEY = Object.fromEntries(VEHICLE_CLASSES.map((v) => [v.name, v.id]));

// Max checked luggage per class - loose upper bounds used to trigger upgrade hints.
// Kept conservative so we only recommend upgrades when clearly needed.
const LUGGAGE_LIMIT = {
  executive_sedan: 3,
  first_class_sedan: 3,
  luxury_suv: 5,
  executive_van: 6,
  luxury_van: 18,
};

/**
 * Smart Vehicle Selector — premium comparison / auto-recommendation cards.
 *  - 1-3 pax : side-by-side comparison (Executive Sedan vs First Class Sedan)
 *  - 4 pax   : auto-select Luxury SUV, single recommendation card
 *  - 5+ pax  : auto-select Luxury Van, single recommendation card
 *
 * Also nudges the customer toward a larger class when luggage or oversized
 * items exceed the recommended vehicle's capacity.
 *
 * Props
 *  - vehicleName : currently selected DISPLAY name (e.g. "Executive Sedan")
 *  - onSelect(name) : called when a new class is picked
 *  - details : the booking form's details object (drives live fare + terminal)
 */
export default function SmartVehicleSelector({ vehicleName, onSelect, details }) {
  const passengers = Number(details?.passengers) || 1;
  const luggage = Number(details?.luggage_count) || 0;
  const oversized = details?.oversized_luggage === "Yes";

  const mode = passengers >= 5 ? "sprinter" : passengers === 4 ? "suv" : "compare";
  const autoKey = mode === "sprinter" ? "luxury_van" : mode === "suv" ? "luxury_suv" : null;
  const currentKey = NAME_TO_KEY[vehicleName];

  // Auto-select vehicle when autoKey is set (pax=4 SUV, pax=5+ Van), while
  // preserving a URL-provided initial vehicle in the compare-mode case.
  // - Mount with autoKey → apply the auto pick (previous bug: it was skipped).
  // - Runtime transition FROM SUV/Van TO compare mode → reset to Executive Sedan
  //   (only if the user is still sitting on the previously auto-picked class).
  const prevAutoKeyRef = useRef(null);
  const hasMountedRef = useRef(false);
  useEffect(() => {
    const isMount = !hasMountedRef.current;
    hasMountedRef.current = true;
    const prev = prevAutoKeyRef.current;
    prevAutoKeyRef.current = autoKey;

    if (autoKey) {
      const autoName = CATALOG[autoKey].name;
      if (vehicleName !== autoName) onSelect(autoName);
      return;
    }

    // autoKey is null. Do not touch on initial mount so URL preselect survives.
    if (isMount) return;

    // Only reset to Executive Sedan when the user was on the previously auto-
    // picked class (transitioning away from SUV/Van to compare mode).
    if (prev === "luxury_suv" && vehicleName === "Luxury SUV") {
      onSelect("Executive Sedan");
    } else if (prev === "luxury_van" && vehicleName === "Luxury Van") {
      onSelect("Executive Sedan");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoKey]);

  const displayedKeys = autoKey
    ? [autoKey]
    : (() => {
        const base = ["executive_sedan", "first_class_sedan"];
        // If the user has already upgraded to a class outside the compare pair,
        // surface it as the single recommended card.
        if (currentKey && !base.includes(currentKey)) return [currentKey];
        return base;
      })();

  const fares = useVehicleFares(details, displayedKeys);

  // Luggage-aware upgrade recommendation.
  // If the currently-recommended vehicle can't reasonably handle the luggage,
  // suggest the next-larger class (never larger than Luxury Van).
  // Do not repeat the nudge once the customer has already moved up to
  // SUV / Van — those classes comfortably handle oversized items.
  const luggageUpgrade = useMemo(() => {
    if (!currentKey) return null;
    const limit = LUGGAGE_LIMIT[currentKey];
    const luggageExceeds = luggage > limit;
    // Suppress the oversized-only nudge for SUV / vans (they're already suitable).
    const isLargeClass =
      currentKey === "luxury_suv" ||
      currentKey === "executive_van" ||
      currentKey === "luxury_van";
    const shouldNudge = luggageExceeds || (oversized && !isLargeClass);
    if (!shouldNudge) return null;
    // Escalation ladder based on current class.
    const upgradeMap = {
      executive_sedan: "luxury_suv",
      first_class_sedan: "luxury_suv",
      luxury_suv: "luxury_van",
      executive_van: "luxury_van",
      luxury_van: null,
    };
    const nextKey = upgradeMap[currentKey];
    if (!nextKey) return null;
    return {
      currentName: CATALOG[currentKey].name,
      suggestedName: CATALOG[nextKey].name,
      reason: oversized && !luggageExceeds ? "oversized" : "luggage",
    };
  }, [currentKey, luggage, oversized]);

  const recommendationCopy =
    mode === "sprinter"
      ? "Selected automatically based on your group size and luggage."
      : mode === "suv"
      ? "Selected automatically based on your passenger count."
      : null;

  return (
    <div className="space-y-6" data-testid="smart-vehicle-selector">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="sjc-eyebrow text-muted-light flex items-center gap-3">
            <span className="sjc-hairline w-6" />
            Your vehicle
          </p>
          <h3 className="font-display mt-3 text-2xl md:text-[28px] text-charcoal leading-tight tracking-tight">
            {mode === "compare" ? (
              <>Choose your <span className="italic font-light">class.</span></>
            ) : (
              <>Recommended <span className="italic font-light">vehicle.</span></>
            )}
          </h3>
        </div>
        {fares.loading && (
          <span className="text-xs text-muted-light/70 flex items-center gap-1.5 italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
            Calculating fare…
          </span>
        )}
      </div>

      <div
        className={cn(
          "grid gap-5",
          mode === "compare" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
        )}
      >
        {displayedKeys.map((key) => (
          <VehicleCard
            key={key}
            vehicle={CATALOG[key]}
            fare={fares.data[key]}
            loading={fares.loading}
            selected={vehicleName === CATALOG[key].name}
            isAuto={!!autoKey}
            onClick={() => !autoKey && onSelect(CATALOG[key].name)}
          />
        ))}
      </div>

      {luggageUpgrade && (
        <div
          data-testid="luggage-upgrade-note"
          className="border-l border-gold pl-5 py-2 flex items-start gap-4"
        >
          <div className="min-w-0 flex-1">
            <p className="sjc-eyebrow text-gold text-[10px]">Concierge Note</p>
            <p className="mt-2 text-sm text-charcoal leading-relaxed font-light" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "16px", fontStyle: "italic" }}>
              Based on your{" "}
              {luggageUpgrade.reason === "oversized"
                ? "oversized luggage"
                : `${luggage} pieces of luggage`}
              , we would recommend the <span className="font-medium not-italic">{luggageUpgrade.suggestedName}</span>{" "}
              for a more comfortable journey.
            </p>
            {!autoKey && (
              <button
                type="button"
                data-testid="luggage-upgrade-btn"
                onClick={() => onSelect(luggageUpgrade.suggestedName)}
                className="mt-3 group inline-flex items-center gap-2.5 text-[10px] font-semibold tracking-[0.24em] uppercase text-muted-light hover:text-charcoal transition-colors duration-300"
              >
                <span>Upgrade to {luggageUpgrade.suggestedName}</span>
                <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
              </button>
            )}
          </div>
        </div>
      )}

      {recommendationCopy && (
        <p
          data-testid="vehicle-recommendation-note"
          className="text-[15px] text-muted-light italic font-light flex items-center gap-3"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          {recommendationCopy}
        </p>
      )}
    </div>
  );
}

function VehicleCard({ vehicle, fare, loading, selected, isAuto, onClick }) {
  return (
    <motion.button
      type="button"
      data-testid={`vehicle-card-${vehicle.id}`}
      data-selected={selected ? "true" : "false"}
      onClick={onClick}
      whileHover={!isAuto ? { y: -2 } : undefined}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "relative w-full text-left overflow-hidden bg-white transition-all duration-500 focus:outline-none border",
        selected
          ? "border-midnight shadow-[0_20px_50px_-20px_rgba(8,11,16,0.35)]"
          : "border-charcoal/12 hover:border-charcoal/40",
        isAuto ? "cursor-default" : "cursor-pointer",
      )}
    >
      {selected && (
        <span
          data-testid={`vehicle-card-${vehicle.id}-check`}
          className="absolute top-4 right-4 w-6 h-6 rounded-full bg-midnight grid place-items-center"
        >
          <Check className="w-3 h-3 text-warm-white" strokeWidth={2.5} />
        </span>
      )}

      <div className="flex gap-5 p-5 md:p-6">
        <div className="w-24 h-24 md:w-32 md:h-32 overflow-hidden bg-ivory flex-shrink-0">
          <img
            src={vehicle.image}
            alt={vehicle.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              // Neutral fallback so a 404'ing stock photo doesn't leave a broken tile.
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-ivory", "to-stone");
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="sjc-eyebrow text-muted-light/70 text-[9px]">
            {vehicle.tagline || "Class"}
          </p>
          <h4 className="font-display mt-1.5 text-xl md:text-2xl text-charcoal leading-tight tracking-tight">
            {vehicle.name}
          </h4>
          <p className="mt-1.5 text-xs text-muted-light font-light line-clamp-1">
            {(vehicle.models && vehicle.models[0]) || vehicle.desc || ""}
          </p>

          <div className="mt-4 flex items-center gap-5 text-xs text-muted-light font-light">
            <span className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
              {vehicle.capacity.passengers}
            </span>
            <span className="flex items-center gap-2">
              <Briefcase className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
              {vehicle.capacity.luggage}
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-charcoal/10 px-5 md:px-6 py-4 flex items-baseline justify-between bg-ivory/40">
        <span className="text-[10px] font-medium tracking-[0.24em] uppercase text-muted-light/70">
          Estimated fare
        </span>
        <span
          data-testid={`vehicle-card-${vehicle.id}-fare`}
          className={cn(
            "font-display text-2xl md:text-[26px] leading-none tracking-tight",
            selected ? "text-charcoal" : "text-charcoal",
          )}
        >
          {loading && !fare ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-light/50" strokeWidth={1.5} />
          ) : fare && fare.success ? (
            money(fare.total)
          ) : (
            <span className="text-xs text-muted-light/70 font-normal italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Enter trip details
            </span>
          )}
        </span>
      </div>
    </motion.button>
  );
}

/** Debounced parallel /api/quote fetches for a list of engine vehicle keys. */
function useVehicleFares(details, vehicleKeys) {
  const basePayload = useMemo(
    () => buildQuotePayload({ vehicleName: "Executive Sedan", details }),
    [details],
  );
  const [state, setState] = useState({ data: {}, loading: false });

  const cacheKey = JSON.stringify({ basePayload, vehicleKeys });

  useEffect(() => {
    if (!basePayload) {
      setState({ data: {}, loading: false });
      return;
    }
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      const results = await Promise.all(
        vehicleKeys.map(async (vk) => {
          try {
            const data = await fetchQuote({ ...basePayload, vehicle_key: vk });
            return [vk, data];
          } catch (_) {
            return [vk, null];
          }
        }),
      );
      if (!active) return;
      setState({ data: Object.fromEntries(results), loading: false });
    }, 400);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [cacheKey]);

  return state;
}

// Utility: caller can convert engine key -> display name.
export const vehicleKeyToName = (k) => CATALOG[k]?.name || null;
export const vehicleNameToKey = (n) => NAME_TO_KEY[n] || null;
